import {
  Component,
  inject,
  OnInit,
  signal
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { emulateClick } from '../../../core/a11y';
import { DrawerService } from '../../../drawer/drawer.service';
import { NotificationService } from '../../../notification/notification.service';
import { ProgressClickDirective } from '../../../shared/directives/progress-click.directive';
import { ByPassPipe } from '../../../shared/pipes/by-pass.pipe';
import {
  AuthService,
  DeletionOutstanding
} from '../../../shared/services/auth.service';

@Component({
  selector: 'app-confirm-delete-account',
  imports: [
    NgxTolgeeModule,
    ProgressClickDirective,
    ByPassPipe,
    DatePipe
  ],
  templateUrl: './confirm-delete-account.component.html'
})
export class ConfirmDeleteAccountComponent implements OnInit {
  readonly emulateClick = emulateClick;
  router = inject(Router);
  authService = inject(AuthService);
  drawerService = inject(DrawerService);
  notificationService = inject(NotificationService);
  outstanding = signal<DeletionOutstanding[]>([]);
  loaded = signal(false);
  // Optimistic until the summary says otherwise: if it fails, the server still refuses the request when disabled
  automatic = signal(true);

  async ngOnInit(): Promise<void> {
    try {
      const summary = await this.authService.deletionSummary();
      this.automatic.set(summary.automatic);
      this.outstanding.set(summary.outstanding);
    } catch (err) {
      // The summary is informative only: never prevent the customer from exercising their right to deletion
      console.error(err);
    } finally {
      this.loaded.set(true);
    }
  }

  /**
   * Format a remaining time credit (minutes) as 00h20
   * @param minutes The remaining minutes
   * @returns The formatted duration
   */
  duration(minutes: number): string {
    return `${String(Math.floor(minutes / 60)).padStart(2, '0')}h${String(minutes % 60).padStart(2, '0')}`;
  }

  /**
   * Ask for the deletion of the account: the customer still has to confirm with the link sent by email
   */
  requestDeletion = async (): Promise<void> => {
    try {
      if (this.automatic()) {
        try {
          await this.authService.requestDeletion();
          this.drawerService.close();
          this.notificationService.open({
            type: 'message',
            title: 'deleteAccountEmailSent',
            message: 'deleteAccountEmailSentDescription'
          });
          return;
        } catch (err) {
          // The summary failed and the automated flow is disabled: the server picks the former flow
          if (!String((err as { code?: unknown }).code ?? '').includes('failed-precondition')) {
            throw err;
          }
        }
      }
      await this.authService.delete();
      this.drawerService.close();
      this.router.navigate([ '/' ], { replaceUrl: true }).catch(err => console.error(err));
      this.notificationService.open({
        type: 'success',
        title: 'deleteAccountSuccess',
        message: 'deleteAccountSuccessDescription',
        close: 3000
      });
    } catch {
      this.notificationService.open({
        type: 'error',
        title: 'cannotDeleteAccount',
        message: 'cannotDeleteAccountDescription'
      });
    }
  }
}
